import { useEffect, useMemo } from 'react';

import { CAST_PILOT_ASSISTANT } from '@cast/constants';

import {
  Alert,
  Button,
  Icons,
  LabelChip,
  LayoutCard,
  MarketingBanner,
  PageHeader,
  Stack,
  Tooltip,
  Typography,
  tokens,
} from '@cast/design-system';

import { raiseReportingMonetizationEvent } from './analytics/reportingMonetizationEvents';
import { ReportingPlanHardUpgradeBanner } from './ReportingPlanHardUpgradeBanner';
import { ReportingPlanSoftUpgradeBanner } from './ReportingPlanSoftUpgradeBanner';
import type { ReportingCheckoutRequest } from './reportingCheckout';
import {
  FREE_VCPU_CAP,
  isOverPlanCap,
  isSoftOverage,
  type ReportingPaidPlanId,
  type ReportingPlan,
} from './reportingPlans';
import {
  formatReportingTrialEndLong,
  formatReportingVcpu,
  freeVcpuCapLabel,
} from './reportingFormat';
import { useReportingPlansWithPrices } from './useReportingPlansWithPrices';
import { useReportingTrial } from './useReportingTrial';

type ReportingPlansFakeDoorProps = {
  avgProvisionedVcpu?: number;
  currentPaidPlanId?: ReportingPaidPlanId;
  onContactSales?: () => void;
  /**
   * Opens Stripe Checkout (external URL from BE). PAID is set by Stripe webhook → BE;
   * caller should refetch monetization state when the user returns.
   */
  onCheckout?: (request: ReportingCheckoutRequest) => void;
  onUpgradeFromSoftNudge?: () => void;
  /** Stripe Customer Portal — current paid plan card CTA. */
  onManageBilling?: () => void;
};

type CapabilityRow = {
  capability: string;
  basic: string;
  full: string;
  basicMuted?: boolean;
  fullMuted?: boolean;
};

const PLATFORM_CAPABILITIES: CapabilityRow[] = [
  { capability: 'Queryable history', basic: 'Unlimited', full: 'Unlimited' },
  { capability: 'Reporting resolution', basic: '1 hour', full: '5 minutes' },
  {
    capability: 'Cluster cost & Efficiency reports',
    basic: 'Included',
    full: 'Included',
  },
  {
    capability: 'Namespace / workload cost reports',
    basic: 'Included',
    full: 'Included',
  },
  {
    capability: 'Cost comparison',
    basic: '7-day periods only',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Savings simulation / potential savings',
    basic: 'Basic view',
    full: 'Full view',
    basicMuted: true,
  },
  {
    capability: 'Allocation groups',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Organization-level rollups',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Network cost',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'GPU cost & utilization',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Storage cost reports',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Reliability reporting',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Anomaly detection, alerts',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  { capability: 'CSV export', basic: 'Included', full: 'Included' },
  {
    capability: 'Prometheus API endpoints',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: CAST_PILOT_ASSISTANT,
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'MCP',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Automation preview',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
  {
    capability: 'Support',
    basic: 'Not included',
    full: 'Included',
    basicMuted: true,
  },
];

const BASIC_REPORTING_TOOLTIP =
  "Applies after your trial if your fleet averages more than 250 vCPU and you haven't chosen a paid plan.";
const FULL_REPORTING_TOOLTIP =
  'Applies while your fleet averages 250 vCPU or less, during a trial, or on a paid plan.';

const splitPriceLabel = (
  priceLabel: string
): { amount: string; suffix: string } => {
  const match = priceLabel.trim().match(/^(\$\d[\d,]*)\s*\/\s*(mo|month)$/i);
  if (!match) {
    return { amount: priceLabel, suffix: '' };
  }
  return { amount: match[1], suffix: ' / mo' };
};

const PlanPrice = ({ priceLabel }: { priceLabel: string }) => {
  const { amount, suffix } = splitPriceLabel(priceLabel);
  return (
    <Typography component="p" sx={{ m: 0 }}>
      <Typography
        component="span"
        variant="P20B"
        color={tokens.theme.fg.structural.primary}
      >
        {amount}
      </Typography>
      {suffix ? (
        <Typography
          component="span"
          variant="P16M"
          color={tokens.theme.fg.structural.primary}
        >
          {suffix}
        </Typography>
      ) : null}
    </Typography>
  );
};

type PlanCta = {
  label: string;
  disabled: boolean;
  variant: 'primary' | 'secondary';
  action: 'checkout' | 'contact_sales' | 'manage' | 'none';
  tooltip?: string;
};

const getOverPlanLimitTooltip = ({
  avgProvisionedVcpu,
  vcpuCap,
}: {
  avgProvisionedVcpu: number;
  vcpuCap: number;
}): string => {
  const fleet =
    avgProvisionedVcpu > 0
      ? `~${formatReportingVcpu(Math.round(avgProvisionedVcpu))} vCPU`
      : 'more vCPU than this plan allows';
  return `Not available — your fleet averages ${fleet}, above this plan’s ${formatReportingVcpu(
    vcpuCap
  )} vCPU limit.`;
};

export const getPlanCardCta = ({
  plan,
  overCap,
  isCurrent,
  avgProvisionedVcpu = 0,
}: {
  plan: ReportingPlan;
  overCap: boolean;
  isCurrent: boolean;
  avgProvisionedVcpu?: number;
}): PlanCta => {
  if (isCurrent) {
    return {
      label: 'Manage subscription',
      disabled: false,
      variant: 'primary',
      action: 'manage',
    };
  }
  if (
    overCap &&
    plan.vcpuCap != null &&
    (plan.cta === 'checkout' || plan.cta === 'continue_free')
  ) {
    return {
      label: 'Over plan limit',
      disabled: true,
      variant: 'secondary',
      action: 'none',
      tooltip: getOverPlanLimitTooltip({
        avgProvisionedVcpu,
        vcpuCap: plan.vcpuCap,
      }),
    };
  }
  if (plan.cta === 'contact_sales') {
    return {
      label: 'Contact sales',
      disabled: false,
      variant: 'secondary',
      action: 'contact_sales',
    };
  }
  if (plan.cta === 'continue_free') {
    return {
      label: 'Continue with Free',
      disabled: false,
      variant: 'secondary',
      action: 'none',
    };
  }
  return {
    label: `Choose ${plan.name}`,
    disabled: false,
    variant: 'secondary',
    action: 'checkout',
  };
};

const CapabilityHeader = ({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}) => (
  <Stack
    component="th"
    direction="row"
    alignItems="center"
    gap={4}
    display="table-cell"
    sx={{
      textAlign: 'left',
      py: 8,
      px: 16,
      borderBottom: tokens.theme.border.structural.neutral,
      verticalAlign: 'middle',
    }}
  >
    <Typography
      component="span"
      variant="L12M"
      color={tokens.theme.fg.structural.secondary}
      sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}
    >
      {label}
    </Typography>
    <Tooltip title={tooltip} placement="top">
      <Icons.Info
        size={14}
        weight="regular"
        color={tokens.theme.fg.structural.secondary}
        style={{ verticalAlign: 'middle', cursor: 'help' }}
      />
    </Tooltip>
  </Stack>
);

export const ReportingPlansFakeDoor = ({
  avgProvisionedVcpu = 0,
  currentPaidPlanId,
  onContactSales,
  onCheckout,
  onUpgradeFromSoftNudge,
  onManageBilling,
}: ReportingPlansFakeDoorProps) => {
  const { expiresAt, expiresInDays, isActive, isEligible, state } =
    useReportingTrial();
  const { plans, enterprisePlan, recommendPlan } =
    useReportingPlansWithPrices();

  const displayPlans = useMemo(
    () => [...plans.filter((plan) => plan.cta === 'checkout'), enterprisePlan],
    [enterprisePlan, plans]
  );

  useEffect(() => {
    if (
      !isEligible &&
      !isActive &&
      state !== 'paid' &&
      state !== 'post-trial'
    ) {
      return;
    }
    raiseReportingMonetizationEvent('reporting_offer_viewed', {
      source: 'platform_usage_plans',
      current_plan:
        state === 'paid'
          ? currentPaidPlanId ?? 'paid'
          : state === 'active'
          ? 'trial'
          : state,
    });
  }, [currentPaidPlanId, isActive, isEligible, state]);

  const recommended = recommendPlan(avgProvisionedVcpu);
  const trialEndsOn = formatReportingTrialEndLong(expiresAt, expiresInDays);
  const isPaid = state === 'paid' || !!currentPaidPlanId;
  const fleetAvgRounded =
    avgProvisionedVcpu > 0 ? Math.round(avgProvisionedVcpu) : undefined;

  const statusBanner = (() => {
    if (isPaid) {
      return null;
    }
    if (state === 'active') {
      const endPhrase = trialEndsOn
        ? trialEndsOn.startsWith('in ')
          ? trialEndsOn
          : `on ${trialEndsOn}`
        : 'soon';
      const avgPart =
        fleetAvgRounded != null
          ? ` Your fleet currently averages ${formatReportingVcpu(
              fleetAvgRounded
            )} vCPU.`
          : '';
      return {
        kind: 'trial' as const,
        testId: 'reporting-plans-banner-trial',
        body: `Your Full Reporting trial ends ${endPhrase}.${avgPart} If it’s still above ${freeVcpuCapLabel()} vCPU when the trial ends, choose a paid plan to keep Full Reporting. Without one, reporting continues on Basic at no charge.`,
      };
    }
    // Figma 580:25976 — free MarketingBanner only while fleet fits Free (≤250).
    if (avgProvisionedVcpu > FREE_VCPU_CAP) {
      return null;
    }
    return {
      kind: 'free' as const,
      testId: 'reporting-plans-banner-free',
      title: 'You’re on free reporting',
      message: `All reporting features are included at no charge while your fleet’s average usage stays below ${freeVcpuCapLabel()} vCPU. If you exceed that limit, your 30-day trial will start automatically.`,
    };
  })();

  const handleSelect = (plan: ReportingPlan, action: PlanCta['action']) => {
    if (action === 'none') {
      return;
    }
    if (action === 'manage') {
      onManageBilling?.();
      return;
    }

    raiseReportingMonetizationEvent('reporting_plan_selected', {
      source: 'platform_usage_plans',
      selected_plan: plan.id,
      selected_bracket: plan.vcpuCapLabel,
      displayed_price: plan.priceLabel,
    });

    if (action === 'contact_sales') {
      onContactSales?.();
      return;
    }

    if (plan.cta !== 'checkout') {
      return;
    }

    onCheckout?.({
      planId: plan.id,
      planName: plan.name,
      vcpuCapLabel: plan.vcpuCapLabel,
      priceLabel: plan.priceLabel,
    });
  };

  const currentPaidPlan = currentPaidPlanId
    ? plans.find((p) => p.id === currentPaidPlanId)
    : undefined;
  const hardOverage =
    currentPaidPlan?.vcpuCap != null &&
    isSoftOverage(avgProvisionedVcpu, currentPaidPlan.vcpuCap);
  const softOverage =
    !hardOverage &&
    currentPaidPlan?.vcpuCap != null &&
    isOverPlanCap(avgProvisionedVcpu, currentPaidPlan.vcpuCap);

  return (
    <Stack gap={24} data-testid="reporting-plans-fake-door">
      <PageHeader heading="Choose your plan" />

      {statusBanner?.kind === 'trial' ? (
        <Alert color="info" size="lg" testId={statusBanner.testId}>
          <Typography variant="P14R">{statusBanner.body}</Typography>
        </Alert>
      ) : null}
      {statusBanner?.kind === 'free' ? (
        <MarketingBanner
          key="reporting-plans-free-banner"
          testId={statusBanner.testId}
          title={statusBanner.title}
          message={statusBanner.message}
          closable
        />
      ) : null}

      {currentPaidPlan?.vcpuCap != null && hardOverage ? (
        <ReportingPlanHardUpgradeBanner
          planId={currentPaidPlan.id}
          planName={currentPaidPlan.name}
          planVcpuCap={currentPaidPlan.vcpuCap}
          avgProvisionedVcpu={avgProvisionedVcpu}
          onUpgrade={onUpgradeFromSoftNudge}
        />
      ) : null}
      {currentPaidPlan?.vcpuCap != null && softOverage ? (
        <ReportingPlanSoftUpgradeBanner
          planId={currentPaidPlan.id}
          planName={currentPaidPlan.name}
          planVcpuCap={currentPaidPlan.vcpuCap}
          avgProvisionedVcpu={avgProvisionedVcpu}
          onUpgrade={onUpgradeFromSoftNudge}
        />
      ) : null}

      <Stack
        direction="row"
        gap={24}
        alignItems="stretch"
        width="100%"
        data-testid="reporting-plan-cards"
        sx={{
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        {displayPlans.map((plan) => {
          const overCap = isOverPlanCap(avgProvisionedVcpu, plan.vcpuCap);
          const isCurrent = currentPaidPlanId === plan.id;
          const isMinimum =
            !isPaid &&
            recommended.id === plan.id &&
            plan.cta === 'checkout' &&
            !overCap;
          const cta = getPlanCardCta({
            plan,
            overCap: overCap && !isCurrent,
            isCurrent,
            avgProvisionedVcpu,
          });

          return (
            <LayoutCard
              key={plan.id}
              sx={{
                flex: '1 1 0',
                minWidth: { xs: '100%', sm: 220, md: 0 },
                display: 'flex',
                flexDirection: 'column',
                minHeight: 248,
                ...(overCap && !isCurrent ? { opacity: 0.72 } : undefined),
                ...(isMinimum
                  ? {
                      outline: `1px solid ${tokens.theme.border.focus}`,
                    }
                  : undefined),
              }}
              testId={`reporting-plan-card-${plan.id}`}
            >
              <Stack gap={16} height="100%" justifyContent="space-between">
                <Stack gap={12}>
                  <Stack gap={4}>
                    <Stack
                      direction="row"
                      gap={8}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="P16B"
                        color={tokens.theme.fg.structural.primary}
                      >
                        {plan.name}
                      </Typography>
                      {isCurrent ? (
                        <LabelChip
                          size="sm"
                          testId={`reporting-plan-current-${plan.id}`}
                          sx={{
                            bgcolor: tokens.theme.bg.status.successSubtle,
                            color: tokens.theme.fg.status.onSuccessSubtle,
                            textTransform: 'uppercase',
                          }}
                        >
                          Current plan
                        </LabelChip>
                      ) : null}
                    </Stack>
                    <Typography
                      variant="P12R"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {plan.vcpuCapLabel}
                    </Typography>
                  </Stack>
                  <PlanPrice priceLabel={plan.priceLabel} />
                </Stack>

                <Stack gap={12}>
                  {isMinimum ? (
                    <Alert
                      color="info"
                      size="sm"
                      noIcon
                      testId={`reporting-plan-minimum-${plan.id}`}
                    >
                      <Typography variant="P12R">
                        Minimum plan for your usage
                      </Typography>
                    </Alert>
                  ) : null}
                  {cta.tooltip ? (
                    <Tooltip title={cta.tooltip} placement="top">
                      <span
                        data-testid={`reporting-plan-cta-tooltip-${plan.id}`}
                        style={{ display: 'block', width: '100%' }}
                      >
                        <Button
                          variant={
                            cta.action === 'manage' || isMinimum
                              ? 'primary'
                              : cta.variant
                          }
                          size="lg"
                          fullWidth
                          disabled={cta.disabled}
                          onClick={() => handleSelect(plan, cta.action)}
                          testId={`reporting-plan-cta-${plan.id}`}
                        >
                          {cta.label}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      variant={
                        cta.action === 'manage' || isMinimum
                          ? 'primary'
                          : cta.variant
                      }
                      size="lg"
                      fullWidth
                      disabled={cta.disabled}
                      onClick={() => handleSelect(plan, cta.action)}
                      testId={`reporting-plan-cta-${plan.id}`}
                    >
                      {cta.label}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </LayoutCard>
          );
        })}
      </Stack>

      <LayoutCard
        title="Platform capabilities"
        testId="reporting-platform-capabilities"
        slotProps={{ title: { variant: 'P18B' } }}
      >
        <Stack
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            display: 'table',
          }}
        >
          <Stack component="thead" display="table-header-group">
            <Stack component="tr" display="table-row">
              <Typography
                component="th"
                variant="L12M"
                color={tokens.theme.fg.structural.secondary}
                sx={{
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  py: 8,
                  px: 16,
                  borderBottom: tokens.theme.border.structural.neutral,
                  display: 'table-cell',
                }}
              >
                Capability
              </Typography>
              <CapabilityHeader
                label="Basic reporting"
                tooltip={BASIC_REPORTING_TOOLTIP}
              />
              <CapabilityHeader
                label="Full reporting"
                tooltip={FULL_REPORTING_TOOLTIP}
              />
            </Stack>
          </Stack>
          <Stack component="tbody" display="table-row-group">
            {PLATFORM_CAPABILITIES.map((row, index) => (
              <Stack
                key={row.capability}
                component="tr"
                display="table-row"
                data-testid={`reporting-capability-row-${row.capability}`}
                sx={{
                  bgcolor:
                    index % 2 === 1
                      ? tokens.theme.bg.structural.surfaceSubtle
                      : undefined,
                }}
              >
                <Typography
                  component="td"
                  variant="P14R"
                  sx={{
                    py: 10,
                    px: 16,
                    borderBottom: tokens.theme.border.structural.neutral,
                    display: 'table-cell',
                  }}
                >
                  {row.capability}
                </Typography>
                <Typography
                  component="td"
                  variant="P14R"
                  color={
                    row.basicMuted
                      ? tokens.theme.fg.structural.secondary
                      : tokens.theme.fg.structural.primary
                  }
                  sx={{
                    py: 10,
                    px: 16,
                    borderBottom: tokens.theme.border.structural.neutral,
                    display: 'table-cell',
                  }}
                >
                  {row.basic}
                </Typography>
                <Typography
                  component="td"
                  variant="P14R"
                  color={
                    row.fullMuted
                      ? tokens.theme.fg.structural.secondary
                      : tokens.theme.fg.structural.primary
                  }
                  sx={{
                    py: 10,
                    px: 16,
                    borderBottom: tokens.theme.border.structural.neutral,
                    display: 'table-cell',
                  }}
                >
                  {row.full}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </LayoutCard>
    </Stack>
  );
};
